/**
 * Component Name: Header
 *
 * Description:
 * The Header component provides the primary navigation bar for the application. It includes a responsive menu structure
 * that supports dropdowns for desktop, a mobile-friendly hamburger menu, and a fixed position for persistent visibility
 * during scrolling. The Header also incorporates dynamic functionality such as highlighting the current page and hiding
 * navigation items based on configuration.
 *
 * Usage:
 * Use the Header component at the top of your application to enable navigation across all sections and pages. The
 * component is configured to adapt to different screen sizes and supports nested navigation links.
 *
 * Example:
 * ```tsx
 * import Header from './Header/Header';
 *
 * const App = () => {
 *   return (
 *     <div>
 *       <Header />
 *       <main>Page Content</main>
 *     </div>
 *   );
 * };
 * ```
 *
 * Props:
 * - The Header component does not accept direct props. It dynamically pulls navigation data from `/data/Sitemap.tsx`.
 *
 * Features:
 * - **Responsive Design:** Automatically adjusts for desktop and mobile views.
 * - **Dynamic Highlighting:** Highlights the active page based on the current URL.
 * - **Dropdown Menus:** Expands to show sub-navigation items on hover for desktop.
 * - **Mobile Drawer:** Includes a collapsible menu for smaller screens.
 * - **Custom Navigation Logic:** Ignores items with `nav: "hide"` from the navigation configuration.
 *
 * Notes:
 * - Navigation items and their configurations (e.g., labels, paths, sub-links) are managed in `/data/Sitemap.tsx`.
 * - Custom classes are added to indicate the active page (`current`) and scrolled state (`scrolled`) for styling flexibility.
 * - The Header supports deep linking for sub-navigation and integrates seamlessly with `react-router-dom` for routing.
 * - Ensure all links are accessible and follow semantic best practices for navigation elements.
 *
 * Example of Rendered HTML:
 * ```html
 * <header class="MuiAppBar-root header-wrapper scrolled">
 *   <div class="logo-wrapper">
 *     <a href="/"><img src="logo.svg" alt="General Genomics Logo"></a>
 *   </div>
 *   <nav class="site-menu">
 *     <div class="header-nav-item">
 *       <button>Our Approach</button>
 *       <div class="dropdown">
 *         <a href="/our-approach/our-capabilities">Our Capabilities</a>
 *       </div>
 *     </div>
 *   </nav>
 *   <button class="MuiIconButton-root"><span class="material-icons">menu</span></button>
 * </header>
 * ```
 *
 * Customization:
 * - Update `/data/Sitemap.tsx` to modify or add navigation items.
 * - Modify styles in `Header.styles.scss` to adjust the layout, colors, or animations.
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { navItems } from '../../data/Sitemap';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';

import './Header.styles.scss';
import logo from '../../assets/images/branding/general-genomics-logo.svg';
import logoBlack from '../../assets/images/branding/genomics_white.png';

const Header: React.FC = () => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownClass, setDropdownClass] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeChildSublink, setActiveChildSublink] = useState<string | null>(
    null
  );
  const location = useLocation();

  const handleToggleChildSublinks = (label: string) => {
    setActiveChildSublink((prev) => (prev === label ? null : label));
  };

  const handleMouseEnter = (label: string) => setOpenDropdown(label);
  const handleMouseLeave = () => setOpenDropdown(null);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Scroll listener to toggle "scrolled" class
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLinkClick = () => {
    setDropdownClass('temphide');
    setTimeout(() => {
      setDropdownClass('');
    }, 50);
  };

  const visibleNavItems = navItems.filter((item) => item.nav !== 'hide');

  const drawer = (
    <Box className="mobile-menu-drawer" onClick={handleDrawerToggle}>
      <Box className="drawer-header">
        <Box className="logo-wrapper">
          <Link to="/">
            <img src={logoBlack} alt="General Genomics Logo" width="160" />
          </Link>
        </Box>
        <IconButton className="drawer-close" onClick={handleDrawerToggle}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </Box>
      <List component="ul" className="menu-ul">
        {visibleNavItems.map((item) => (
          <ListItem key={item.label} component="li">
            <Link
              to={item.path}
              className={`page-${item.type || 'group'} ${
                location.pathname.startsWith(item.path) ? 'current' : ''
              }`}
              onClick={handleDrawerToggle}
            >
              <ListItemText className="mobile-label" primary={item.label} />
            </Link>
            {item.subLinks && (
              <List component="ul">
                {item.subLinks
                  .filter((subLink) => subLink.nav !== 'hide')
                  .map((subLink) => (
                    <ListItem key={subLink.label} component="li">
                      <Box
                        className={`mobile-menu-items ${
                          activeChildSublink === subLink.label
                            ? 'active-child-sublink'
                            : ''
                        }`}
                      >
                        <Link
                          to={subLink.path}
                          className={`mobile-page-${subLink.type} ${
                            location.pathname === subLink.path ? 'current' : ''
                          }`}
                          onClick={handleLinkClick}
                        >
                          <Icon icon={subLink.icon} />
                          <span className="mobile-label">{subLink.label}</span>
                        </Link>
                        {subLink.childSublinks && (
                          <Box
                            className={`mobile-arrow-button ${
                              location.pathname === subLink.path
                                ? 'current'
                                : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleChildSublinks(subLink.label);
                            }}
                          >
                            {activeChildSublink === subLink.label ? (
                              <KeyboardArrowUpRoundedIcon />
                            ) : (
                              <KeyboardArrowDownRoundedIcon />
                            )}
                          </Box>
                        )}
                      </Box>
                      {subLink.childSublinks &&
                        activeChildSublink === subLink.label && (
                          <>
                            <Box className="mobile-child-page-items">
                              {subLink.childSublinks.map((childSublink) => (
                                <Link
                                  key={childSublink.label}
                                  to={childSublink.path}
                                  className={`page-${childSublink.type} ${
                                    location.pathname === childSublink.path
                                      ? 'current'
                                      : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLinkClick();
                                  }}
                                >
                                  <span className="child-mobile-label">
                                    {childSublink.label}
                                  </span>
                                </Link>
                              ))}
                            </Box>
                          </>
                        )}
                    </ListItem>
                  ))}
              </List>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <div>
      <AppBar position="fixed" className={isScrolled ? 'scrolled' : ''}>
        <Toolbar className="header-wrapper">
          <Box className="logo-wrapper">
            <Link to="/">
              <img
                src={!mobileOpen ? logo : logoBlack}
                alt="General Genomics Logo"
                width="160"
              />
            </Link>
          </Box>

          {/* Desktop Navigation */}
          <Box
            className="site-menu"
            sx={{ display: { xs: 'none', lg: 'flex' } }}
          >
            {visibleNavItems.map((item) => (
              <Box
                key={item.label}
                className={`header-nav-item ${
                  location.pathname.startsWith(item.path) ? 'current' : ''
                }`}
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <Button
                  label={item.label}
                  url={item.path}
                  type="nav-link"
                  icon={item.subLinks ? 'keyboard_arrow_down' : undefined}
                />

                {item.subLinks && (
                  <Box
                    className={`dropdown ${dropdownClass} ${
                      openDropdown === item.label ? '' : 'hide'
                    }`}
                  >
                    {item.subLinks
                      .filter((subLink) => subLink.nav !== 'hide')
                      .map((subLink) => (
                        <Link
                          key={subLink.label}
                          to={subLink.path}
                          className={`page-${subLink.type} ${
                            location.pathname === subLink.path ? 'current' : ''
                          }`}
                          onClick={handleLinkClick}
                        >
                          <Icon icon={subLink.icon} />
                          <span className="label">{subLink.label}</span>
                          <span className="desc">{subLink.desc}</span>
                        </Link>
                      ))}
                  </Box>
                )}
                {item.subLinks && (
                  <Box
                    className={`dropdown ${dropdownClass} ${
                      openDropdown === item.label ? '' : 'hide'
                    }`}
                  >
                    {item.subLinks
                      .filter((subLink) => subLink.nav !== 'hide')
                      .map((subLink) => {
                        return (
                          <>
                            <Box className="page-items">
                              <Link
                                key={subLink.label}
                                to={subLink.path}
                                className={`page-${subLink.type} ${
                                  location.pathname === subLink.path
                                    ? 'current'
                                    : ''
                                }`}
                                onClick={handleLinkClick}
                              >
                                <Icon icon={subLink.icon} />
                                <span className="label">{subLink.label}</span>
                              </Link>

                              {subLink.childSublinks && (
                                <Box
                                  className={`arrow-button ${
                                    location.pathname === subLink.path
                                      ? 'current'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleToggleChildSublinks(subLink.label)
                                  }
                                ></Box>
                              )}
                            </Box>

                            {subLink.childSublinks && (
                              <Divider sx={{ my: 1 }} />
                            )}

                            {subLink.childSublinks && (
                              <Box
                                className={`child-page-items ${
                                  activeChildSublink === subLink.label
                                    ? 'show-children'
                                    : 'hide-children'
                                }`}
                              >
                                {subLink.childSublinks.map((childSublink) => (
                                  <Link
                                    key={childSublink.label}
                                    to={childSublink.path}
                                    className={`page-${childSublink.type} ${
                                      location.pathname === childSublink.path
                                        ? 'current'
                                        : ''
                                    }`}
                                    onClick={handleLinkClick}
                                  >
                                    <span className="child-label">
                                      {childSublink.label}
                                    </span>
                                  </Link>
                                ))}
                              </Box>
                            )}
                          </>
                        );
                      })}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Contact Us button - hidden on small screens */}
          <Box
            className="header-cta"
            sx={{ display: { xs: 'none', lg: 'flex' } }}
          >
            <Button url="/contact-us" label="Contact Us" />
          </Box>

          {/* Mobile Navigation (Hamburger Menu) */}
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{ display: { xs: 'flex', lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>

        <Drawer
          className="mobile-menu-drawer-wrapper"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              opacity: '100%',
              backgroundColor: 'transparent',
            },
          }}
        >
          {drawer}
        </Drawer>
      </AppBar>
    </div>
  );
};

export default Header;
